// Kopie z FofrLamaPOS (lib/zidle.js) — editor stolů je v pokladně i ve Správě OS
// (Správa → Provozovna → Stoly, 2026-10-20). Měň obojí stejně.
/**
 * Židle kolem stolu — kolik jich je a kde přesně sedí.
 *
 * Ukládá se jen počet u každé strany (`{ nahore, dole, vlevo, vpravo }`
 * u obdélníku a čtverce, `{ kolem }` u kruhu a oválu). Poloha se dopočítá:
 * židle se rozmístí po straně rovnoměrně a přichytí se ke stolu, takže jsou
 * vždycky zarovnané — ručně posunutá židle by se dřív nebo později rozjela.
 *
 * Vše v jednotkách plátna (jako `pozice` stolu), měřítko si aplikuje kreslení.
 */

/** Rozměr židle a mezera od hrany stolu (jednotky plátna). */
export const ZIDLE = { sirka: 26, hloubka: 14, mezera: 5 }

export const KULATE = ['kruh', 'oval']
/** Tvary, které drží stejnou šířku a výšku. */
export const ROVNOSTRANNE = ['ctverec', 'kruh']

export const STRANY = ['nahore', 'dole', 'vlevo', 'vpravo']

/** Židle stolu — uložené, nebo dopočtené z kapacity (starší data). */
export function zidleStolu(stul) {
  const tvar = stul.pozice?.tvar ?? 'obdelnik'
  const z = stul.zidle
  if (z && typeof z === 'object') return normalizovat(z, tvar)
  const k = Number(stul.kapacita) || 0
  return normalizovat({ nahore: Math.ceil(k / 2), dole: Math.floor(k / 2), kolem: k }, tvar)
}

/** Počty pro daný tvar — při přepnutí tvaru se židle převedou, neztratí. */
export function normalizovat(z, tvar) {
  const n = (k) => Math.max(0, Math.min(30, Number(z?.[k]) || 0))
  const soucetStran = n('nahore') + n('dole') + n('vlevo') + n('vpravo')
  if (KULATE.includes(tvar)) return { kolem: n('kolem') || soucetStran }
  if (soucetStran === 0 && n('kolem') > 0) {
    const k = n('kolem')
    return { nahore: Math.ceil(k / 2), dole: Math.floor(k / 2), vlevo: 0, vpravo: 0 }
  }
  return { nahore: n('nahore'), dole: n('dole'), vlevo: n('vlevo'), vpravo: n('vpravo') }
}

export function pocetZidli(z) {
  return Object.values(z ?? {}).reduce((a, b) => a + (Number(b) || 0), 0)
}

/**
 * Kde židle sedí — středy a natočení relativně k levému hornímu rohu stolu.
 *
 * @returns {Array<{ strana: string, cx: number, cy: number, uhel: number }>}
 *   `uhel` 0 = židle leží vodorovně nad stolem (opěradlo nahoře).
 */
export function rozmistit(sirka, vyska, tvar, z) {
  const { hloubka, mezera } = ZIDLE
  const out = []
  const odstup = mezera + hloubka / 2

  if (tvar === 'oval') return rozmistitOval(sirka, vyska, z.kolem ?? 0, odstup)

  if (KULATE.includes(tvar)) {
    const n = z.kolem ?? 0
    const rx = sirka / 2 + odstup
    const ry = vyska / 2 + odstup
    for (let i = 0; i < n; i++) {
      // Od horního středu po směru hodinových ručiček.
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n
      out.push({
        strana: 'kolem',
        cx: sirka / 2 + rx * Math.cos(a),
        cy: vyska / 2 + ry * Math.sin(a),
        uhel: (a * 180) / Math.PI + 90
      })
    }
    return out
  }

  const radek = (n, delka, bod) => {
    for (let i = 0; i < n; i++) out.push(bod(((i + 0.5) * delka) / n))
  }
  radek(z.nahore ?? 0, sirka, (t) => ({ strana: 'nahore', cx: t, cy: -odstup, uhel: 0 }))
  radek(z.dole ?? 0, sirka, (t) => ({ strana: 'dole', cx: t, cy: vyska + odstup, uhel: 180 }))
  radek(z.vlevo ?? 0, vyska, (t) => ({ strana: 'vlevo', cx: -odstup, cy: t, uhel: 270 }))
  radek(z.vpravo ?? 0, vyska, (t) => ({ strana: 'vpravo', cx: sirka + odstup, cy: t, uhel: 90 }))
  return out
}

/**
 * Ovál = obdélník s půlkruhovými konci. Židle sedí jako u skutečného
 * oválného stolu: po jedné na každém kulatém konci (od tří židlí), zbytek
 * rovnoměrně podél rovných dlouhých stran — nahoře o jednu víc při lichém.
 */
function rozmistitOval(sirka, vyska, n, odstup) {
  const vodorovny = sirka >= vyska
  const delka = Math.max(sirka, vyska)
  const r = Math.min(sirka, vyska) / 2
  const L = Math.max(delka - 2 * r, r) // rovný úsek (u skoro kruhu aspoň kousek)
  const R = r + odstup
  const out = []
  const pridej = (px, py, uhel) => {
    // Svislý ovál = otočený o 90°.
    if (!vodorovny) [px, py, uhel] = [-py, px, uhel + 90]
    out.push({ strana: 'kolem', cx: sirka / 2 + px, cy: vyska / 2 + py, uhel })
  }

  const konce = n >= 3 ? 2 : 0
  const zbytek = n - konce
  const nahore = Math.ceil(zbytek / 2)
  const dole = zbytek - nahore
  for (let i = 0; i < nahore; i++) pridej(-L / 2 + ((i + 0.5) * L) / nahore, -R, 0)
  if (konce) pridej(L / 2 + R, 0, 90)
  for (let i = 0; i < dole; i++) pridej(L / 2 - ((i + 0.5) * L) / dole, R, 180)
  if (konce) pridej(-L / 2 - R, 0, 270)
  return out
}
